import { detect } from 'detect-browser';
export const supportedBrowsers = [
    { name: 'chrome', version: '70' },
    { name: 'firefox', version: '60' },
    { name: 'edge-chromium', version: '86' },
    { name: 'opera', version: '66' },
];
export const detectBrowser = () => {
    const info = detect();
    if (info === null) {
        return null;
    }
    const correctedInfo = Object.assign({}, info);
    correctedInfo.version = info.version === null ? null : info.version.split('.')[0];
    return correctedInfo;
};
const parseUaChPlatformVersionMajor = (platformVersion) => {
    if (platformVersion === undefined || platformVersion === '') {
        return null;
    }
    const major = Number(platformVersion.split('.')[0]);
    return Number.isNaN(major) ? null : major;
};
/**
 * Human-readable OS for bug reports. Windows 11 is still reported as NT 10.0 in the
 * legacy user-agent string; use UA-CH when available.
 * @see https://learn.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11
 */
export const resolveOperatingSystem = async () => {
    var _a, _b;
    const uaBrowser = detectBrowser();
    const fallback = (_a = uaBrowser === null || uaBrowser === void 0 ? void 0 : uaBrowser.os) !== null && _a !== void 0 ? _a : 'unknown';
    const uaData = navigator.userAgentData;
    if ((uaData === null || uaData === void 0 ? void 0 : uaData.getHighEntropyValues) === undefined) {
        return fallback;
    }
    try {
        const hints = await uaData.getHighEntropyValues(['platform', 'platformVersion']);
        if (((_b = hints.platform) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === 'windows') {
            const major = parseUaChPlatformVersionMajor(hints.platformVersion);
            if (major !== null && major >= 13) {
                return 'Windows 11';
            }
            if (major !== null && major >= 10 && major < 13) {
                return 'Windows 10';
            }
        }
    }
    catch (_c) {
        // ignore — fall back to UA string parsing
    }
    return fallback;
};
// NOTE: We decided to make exception for iOS 15+, Mac 12+ and Safari 15+.
// The version of Safari equals the OS version. Below these version numbers
// Beey is not supported. The user has to be informed that Beey is in beta
// version for all Apple devices with version 15+.
const checkPartiallySupportedBrowsers = (browser) => {
    if (browser === null || browser.version === null || browser.os === null) {
        return {
            access: 'warn',
            reason: 'uknown',
        };
    }
    // NOTE: These conditions cover Apple computers.
    if (browser.name === 'safari' && Number(browser.version) >= 15) {
        return {
            access: 'warn',
            reason: 'safari',
        };
    }
    if (browser.os === 'Mac OS'
        && (Number(browser.version) >= 13 && Number(browser.version) < 15)) {
        return {
            access: 'warn',
            reason: 'apple',
        };
    }
    if (browser.os === 'Mac OS' && Number(browser.version) < 13) {
        return {
            access: 'deny',
            reason: 'old-apple',
        };
    }
    // NOTE: These conditions cover Apple tablets and phones, for which all browsers
    // are disguised Safari browser.
    if (browser.name === 'ios' && Number(browser.version) >= 15) {
        return {
            access: 'warn',
            reason: 'apple',
        };
    }
    if (browser.os === 'iOS' && Number(browser.version) < 15) {
        return {
            access: 'deny',
            reason: 'old-apple',
        };
    }
    return {
        access: 'warn',
        reason: 'uknown',
    };
};
const checkBrowserSupport = (browser) => {
    if (browser === null || browser.version === null || browser.os === null) {
        return {
            access: 'warn',
            reason: 'uknown',
        };
    }
    const foundSupportedBrowser = supportedBrowsers.find((supportedBrowser) => supportedBrowser.name === browser.name);
    if (foundSupportedBrowser !== undefined) {
        if (Number(browser.version) >= Number(foundSupportedBrowser.version)) {
            return { access: 'ok' };
        }
        return {
            access: 'deny',
            reason: 'old-browser',
        };
    }
    return checkPartiallySupportedBrowsers(browser);
};
export const getBrowserStatus = () => {
    const browser = detectBrowser();
    global.logger.info('browser info', {
        browser,
        windowSize: {
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
        },
    });
    return checkBrowserSupport(browser);
};
